import React, { useState } from 'react';
import styled from 'styled-components';
import Text from 'components/atoms/Text';
import Button from 'components/atoms/Button';
import Icon from 'components/atoms/Icon';
import { COLOR_SYSTEM } from 'components/atoms';
import { NhisStatus } from 'types/nhis';
import { PrescriptionGroup } from 'common/models/types';
import { deliverPrescriptions } from 'api/nhisService';
import { log, LogLevel } from 'utils/LogUtil';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const EmptyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 24px;
  text-align: center;
  gap: 20px;
  flex: 1;
`;

const GroupCard = styled.div<{ $selected: boolean }>`
  border: 2px solid ${(props) =>
    props.$selected ? COLOR_SYSTEM.get('Blue')?.[300] : COLOR_SYSTEM.get('Gray')?.[200]};
  border-radius: 12px;
  padding: 16px;
  background: ${(props) =>
    props.$selected ? COLOR_SYSTEM.get('Blue')?.[25] : '#ffffff'};
  cursor: pointer;
  transition: all 0.2s ease;
`;

const GroupHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
`;

const MedicationItem = styled.div`
  padding: 4px 0 4px 32px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Summary = styled.div`
  background: ${COLOR_SYSTEM.get('Gray')?.[50]};
  border-radius: 8px;
  padding: 16px;
  text-align: center;
`;

const ErrorMessage = styled.div`
  background: ${COLOR_SYSTEM.get('Red')?.[50]};
  border: 1px solid ${COLOR_SYSTEM.get('Red')?.[200]};
  border-radius: 8px;
  padding: 12px 16px;
`;

interface MedicationListPageProps {
  token: string;
  prescriptionGroups: PrescriptionGroup[];
  onStatusChange: (status: NhisStatus, data?: any) => void;
}

const formatDate = (date?: Date | string): string => {
  if (!date) return '';
  const str = typeof date === 'string' ? date : date.toISOString().slice(0, 10);
  if (/^\d{8}$/.test(str)) {
    return str.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3');
  }
  return str.slice(0, 10);
};

const MedicationListPage: React.FC<MedicationListPageProps> = ({ token, prescriptionGroups, onStatusChange }) => {
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(
    () => new Set(prescriptionGroups.map((_, idx) => idx))
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 페이지 새로고침 등으로 메모리의 처방전 목록이 화면에서 날아간 경우.
  // (조회 결과가 진짜 0건인 경우는 백엔드가 DELIVERED로 종결해 이 화면에 도달하지 않는다.)
  // "이미 인증이 완료되어"가 "이미 인증된 번호라 거부됐다"로 오해되던 문구를 정정한다.
  if (prescriptionGroups.length === 0) {
    return (
      <EmptyContainer>
        <div style={{
          width: '64px',
          height: '64px',
          borderRadius: '50%',
          background: COLOR_SYSTEM.get('Orange')?.[100],
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <Icon name="Exclamation Mark Circle" size={32} color={COLOR_SYSTEM.get('Orange')?.[500]} />
        </div>

        <Text textType="H3" color={COLOR_SYSTEM.get('Gray')?.[800]}>
          처방전 목록이 화면에서 사라졌습니다
        </Text>

        <div style={{
          padding: '16px 20px',
          backgroundColor: COLOR_SYSTEM.get('Orange')?.[50],
          border: `1px solid ${COLOR_SYSTEM.get('Orange')?.[200]}`,
          borderRadius: '12px',
          width: '100%',
          maxWidth: '400px',
        }}>
          <Text textType="Body1" color={COLOR_SYSTEM.get('Orange')?.[700]} style={{ fontSize: '15px', lineHeight: '1.8', display: 'block' }}>
            조회 결과는 보안상 화면에만 잠시 보관되며 저장되지 않습니다.
            페이지를 새로고침하시면 목록이 사라져 본인인증부터 다시 진행해야 합니다.
          </Text>
        </div>

        <div style={{ width: '100%', maxWidth: '300px' }}>
          <Button
            buttonType="button"
            color="Primary"
            size="Large"
            type="Text"
            variant="Contained"
            onClick={() => onStatusChange('PENDING')}
          >
            다시 인증하고 불러오기
          </Button>
        </div>
      </EmptyContainer>
    );
  }

  const toggleGroup = (index: number) => {
    setSelectedIndices((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const selectedCount = selectedIndices.size;
  const selectedMedicationCount = prescriptionGroups
    .filter((_, idx) => selectedIndices.has(idx))
    .reduce((sum, g) => sum + (g.prescriptions?.length || 0), 0);

  const handleDeliver = async () => {
    if (selectedCount === 0 || submitting) return;

    setSubmitting(true);
    setError(null);

    const selectedGroups = prescriptionGroups.filter((_, idx) => selectedIndices.has(idx));

    const result = await deliverPrescriptions({
      token,
      prescriptionGroups: selectedGroups,
    });

    if (result.error) {
      log(LogLevel.ERROR, 'MedicationListPage: 처방전 전달 실패', result);
      setError(result.message || '처방전 전달에 실패했습니다.');
      setSubmitting(false);
      return;
    }

    setSubmitting(false);
    onStatusChange('DELIVERED');
  };

  return (
    <Container>
      <Text textType="H2" color={COLOR_SYSTEM.get('Gray')?.[800]}>
        3단계: 처방전 전달
      </Text>
      <Text textType="Body1" color={COLOR_SYSTEM.get('Gray')?.[600]}>
        약사님께 전달할 처방전을 선택해주세요.
        <br />
        정확한 상담을 위해 모든 처방전을 전달하시는 것을 권장합니다.
      </Text>

      {prescriptionGroups.map((group, idx) => (
        <GroupCard
          key={group.groupId || idx}
          $selected={selectedIndices.has(idx)}
          onClick={() => toggleGroup(idx)}
        >
          <GroupHeader>
            <input
              type="checkbox"
              checked={selectedIndices.has(idx)}
              onChange={() => toggleGroup(idx)}
              onClick={(e) => e.stopPropagation()}
              style={{ width: '22px', height: '22px', flexShrink: 0, accentColor: COLOR_SYSTEM.get('Blue')?.[500] }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Text textType="Body1" color={COLOR_SYSTEM.get('Gray')?.[800]} style={{ fontSize: '17px', fontWeight: 600 }}>
                {group.hospital || '병원명 없음'}
              </Text>
              <Text textType="Body2" color={COLOR_SYSTEM.get('Gray')?.[500]}>
                {formatDate(group.prescriptionDate)}
              </Text>
            </div>
          </GroupHeader>
          {(group.prescriptions || []).map((prescription, pIdx) => (
            <MedicationItem key={prescription.id || pIdx}>
              <Text textType="Body2" color={COLOR_SYSTEM.get('Gray')?.[400]}>
                {pIdx < (group.prescriptions?.length || 0) - 1 ? '├' : '└'}
              </Text>
              <Text textType="Body1" color={COLOR_SYSTEM.get('Gray')?.[700]}>
                {prescription.medicationName || '약품명 없음'}
              </Text>
            </MedicationItem>
          ))}
        </GroupCard>
      ))}

      {selectedCount > 0 && (
        <Summary>
          <Text textType="Body1" color={COLOR_SYSTEM.get('Gray')?.[600]}>
            선택한 처방전 {selectedCount}건 (약물 {selectedMedicationCount}개)을
            약사님께 전달합니다.
          </Text>
        </Summary>
      )}

      {error && (
        <ErrorMessage>
          <Text textType="Body2" color={COLOR_SYSTEM.get('Red')?.[600]}>
            {error}
          </Text>
        </ErrorMessage>
      )}

      <Button
        buttonType="button"
        color="Primary"
        size="Large"
        type="Text"
        variant="Contained"
        onClick={handleDeliver}
        disabled={selectedCount === 0 || submitting}
      >
        {submitting ? '전달 중...' : '약사님께 전달하기'}
      </Button>
    </Container>
  );
};

export default MedicationListPage;
